import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Minus } from 'lucide-react'
import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'

const FAQ = () => {
  const { t, language } = useLanguage()
  const [openIndex, setOpenIndex] = useState(null)

  const faqs = language === 'sq' ? [
    {
      question: 'Sa kohë duhet për të ndërtuar një website?',
      answer: 'Një website standard zgjat 2-4 javë. Website më komplekse me e-commerce ose funksionalitete të avancuara mund të zgjatin 6-8 javë.'
    },
    {
      question: 'A ofroni mirëmbajtje pas përfundimit të projektit?',
      answer: 'Po! Ofrojmë paketa mirëmbajtjeje mujore që përfshijnë update, backup, dhe support teknik 24/7.'
    },
    {
      question: 'Sa kushton një projekt typical?',
      answer: 'Çmimet variojnë bazuar në kompleksitetin e projektit. Një website i thjeshtë fillon nga €500, ndërsa projekte më të mëdha fillojnë nga €2000+. Na kontaktoni për një ofertë të personalizuar.'
    },
    {
      question: 'A mund të menaxhoni social media për biznesin tim?',
      answer: 'Absolutisht! Ofrojmë menaxhim të plotë të social media duke përfshirë krijim përmbajtjeje, postim të rregullt, dhe analizë të performancës.'
    },
    {
      question: 'A punoni me biznese ndërkombëtare?',
      answer: 'Po, punojmë me klientë në të gjithë botën. Komunikimi bëhet online dhe procesi është i njëjtë pavarësisht vendndodhjes.'
    },
    {
      question: 'Çfarë duhet të përgatit para se të fillojmë projektin?',
      answer: 'Duhen logo, ngjyrat e brandeve, përmbajtja tekstuale (nëse keni), dhe imazhe. Nëse nuk i keni, mund t\'ju ndihmojmë edhe me këto!'
    },
    {
      question: 'A pranoni pagesë me këste?',
      answer: 'Po, ofrojmë plane pagesash të personalizuara sipas projektit. Zakonisht kërkohet 50% deposit dhe 50% pas përfundimit.'
    },
    {
      question: 'Sa shpejt mund të fillojmë projektin?',
      answer: 'Mund të fillojmë brenda 1-3 ditëve pas nënshkrimit të kontratës dhe marrjes së materialeve të nevojshme.'
    }
  ] : [
    {
      question: 'How long does it take to build a website?',
      answer: 'A standard website takes 2-4 weeks. More complex websites with e-commerce or advanced features may take 6-8 weeks.'
    },
    {
      question: 'Do you offer maintenance after project completion?',
      answer: 'Yes! We offer monthly maintenance packages that include updates, backups, and 24/7 technical support.'
    },
    {
      question: 'How much does a typical project cost?',
      answer: 'Prices vary based on project complexity. A simple website starts from €500, while larger projects start from €2000+. Contact us for a personalized quote.'
    },
    {
      question: 'Can you manage social media for my business?',
      answer: 'Absolutely! We offer complete social media management including content creation, regular posting, and performance analysis.'
    },
    {
      question: 'Do you work with international businesses?',
      answer: 'Yes, we work with clients worldwide. Communication is online and the process is the same regardless of location.'
    },
    {
      question: 'What do I need to prepare before starting the project?',
      answer: 'You need a logo, brand colors, textual content (if available), and images. If you don\'t have them, we can help with these too!'
    },
    {
      question: 'Do you accept installment payments?',
      answer: 'Yes, we offer personalized payment plans depending on the project. Usually 50% deposit and 50% after completion is required.'
    },
    {
      question: 'How soon can we start the project?',
      answer: 'We can start within 1-3 days after contract signing and receiving necessary materials.'
    }
  ]

  return (
    <section className="py-20 bg-gradient-to-br from-dark via-bronze-950/50 to-dark">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-title">
            {language === 'sq' ? 'Pyetje të' : 'Frequently Asked'} <span className="text-gradient-gold">{language === 'sq' ? 'Shpeshta' : 'Questions'}</span>
          </h2>
          <p className="text-xl text-beige-200 mt-4 max-w-2xl mx-auto">
            {language === 'sq' ? 'Gjej përgjigjet për pyetjet më të zakonshme' : 'Find answers to the most common questions'}
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-dark/50 backdrop-blur-sm border border-gold-500/20 rounded-xl overflow-hidden hover:border-gold-500/40 transition-all"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gold-500/5 transition-colors"
              >
                <span className="text-lg font-semibold text-cream pr-4">{faq.question}</span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus className="w-5 h-5 text-gold-400" />
                  ) : (
                    <Plus className="w-5 h-5 text-gold-400" />
                  )}
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 pt-2 text-beige-200 leading-relaxed border-t border-gold-500/10">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-beige-200 mb-4">
            {language === 'sq' ? 'Nuk gjetët përgjigjen që kërkonit?' : 'Didn\'t find the answer you were looking for?'}
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-3 bg-gradient-gold text-dark font-semibold rounded-lg hover:shadow-glow transition-all duration-300"
          >
            {language === 'sq' ? 'Na Kontaktoni' : 'Contact Us'}
          </a>
        </motion.div>
      </div>
    </section>
  )
}

export default FAQ
